import { AlertTriangle, RefreshCw, Sparkles } from 'lucide-react';
import { motion } from 'motion/react';
import { type ReactNode } from 'react';

import { type WidgetMeta } from './types';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';


export interface WidgetContainerProps {
  meta?: Pick<WidgetMeta, 'name' | 'description'>;
  title?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  className?: string;
  contentClassName?: string;

  // States — set one, and the container handles the visual.
  loading?: boolean;
  loadingSkeleton?: ReactNode;
  error?: { message?: string; code?: string } | Error | null | undefined;
  onRetry?: () => void;
  empty?: boolean;
  emptyTitle?: string;
  emptyDescription?: ReactNode;
  emptyCta?: ReactNode;

  children?: ReactNode;
}

export function WidgetContainer({
  meta,
  title,
  icon,
  actions,
  className,
  contentClassName,
  loading,
  loadingSkeleton,
  error,
  onRetry,
  empty,
  emptyTitle,
  emptyDescription,
  emptyCta,
  children,
}: WidgetContainerProps) {
  const heading = title ?? meta?.name ?? 'Widget';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
      className="h-full"
    >
      <Card className={cn('flex h-full flex-col', className)}>
        <CardHeader className="flex flex-row items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 text-ugin-accent" aria-hidden="true">
              {icon ?? <Sparkles className="size-4" />}
            </div>
            <div>
              <h2 className="font-display text-lg leading-none tracking-wide text-ugin-text">
                {heading}
              </h2>
              {meta?.description ? (
                <p className="mt-1 text-xs text-ugin-text-muted">{meta.description}</p>
              ) : null}
            </div>
          </div>
          {actions ? <div className="flex items-center gap-1">{actions}</div> : null}
        </CardHeader>

        <CardContent className={cn('flex-1', contentClassName)}>
          {loading ? (
            loadingSkeleton ?? <DefaultSkeleton />
          ) : error ? (
            <ErrorState error={error} {...(onRetry && { onRetry })} />
          ) : empty ? (
            <EmptyState {...(emptyTitle !== undefined && { title: emptyTitle })} description={emptyDescription} cta={emptyCta} />
          ) : (
            children
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function DefaultSkeleton() {
  return (
    <div className="space-y-3" aria-busy="true" aria-live="polite">
      <Skeleton className="h-4 w-3/5" />
      <Skeleton className="h-4 w-4/5" />
      <Skeleton className="h-4 w-2/5" />
    </div>
  );
}

function ErrorState({
  error,
  onRetry,
}: {
  error: { message?: string; code?: string } | Error;
  onRetry?: () => void;
}) {
  const message =
    error instanceof Error
      ? error.message
      : (error.message ?? 'Something went wrong loading this widget.');
  return (
    <div className="flex flex-col items-start gap-3 text-sm" role="alert">
      <div className="flex items-center gap-2 text-ugin-danger">
        <AlertTriangle className="size-4" />
        <span className="font-medium">{message}</span>
      </div>
      {onRetry ? (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="size-3.5" />
          Try again
        </Button>
      ) : null}
    </div>
  );
}

function EmptyState({
  title,
  description,
  cta,
}: {
  title?: string;
  description?: ReactNode;
  cta?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-2 text-sm text-ugin-text-muted">
      {title ? <p className="font-display text-base text-ugin-text">{title}</p> : null}
      {description ? <p className="max-w-prose leading-relaxed">{description}</p> : null}
      {cta ? <div className="mt-2">{cta}</div> : null}
    </div>
  );
}
